import { getAuthToken } from "components/common/authTokenManager";
import { v2Request } from "helpers/request";
import { CaresSubmitPayload, CaresSubmitResponse } from "components/common/myRepCares/types";
import { CaresReportsListFilters } from "components/admin/caresRedesign/types";

const { REACT_APP_V2_API_ENDPOINT } = process.env;

/**
 * Uses a raw fetch instead of v2Request because v2Request always sets
 * Content-Type: application/json, which would break the multipart/form-data boundary needed to
 * send the attached images.
 */
export const submitCaresReport = async (
  payload: CaresSubmitPayload,
): Promise<CaresSubmitResponse> => {
  const token = getAuthToken();

  const formData = new FormData();
  formData.append("type", payload.type);
  formData.append("details", payload.details);
  if (payload.severity) formData.append("severity", payload.severity);
  formData.append("contextType", payload.contextType);
  if (payload.contextPath) formData.append("contextPath", payload.contextPath);
  formData.append("userAgent", payload.userAgent);
  formData.append("viewportWidth", String(payload.viewportWidth));
  formData.append("viewportHeight", String(payload.viewportHeight));
  if (payload.firstName) formData.append("firstName", payload.firstName);
  if (payload.lastName) formData.append("lastName", payload.lastName);
  if (payload.email) formData.append("email", payload.email);
  if (payload.phone) formData.append("phone", payload.phone);
  payload.images.forEach((file) => formData.append("images", file));

  try {
    const res = await fetch(`${REACT_APP_V2_API_ENDPOINT}/cares/submit`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      // No Content-Type header — browser sets multipart/form-data with boundary
      headers: { "X-Token": token || "" },
      body: formData,
    });
    return await res.json().catch(() => ({ isSuccessful: false }));
  } catch {
    return { isSuccessful: false, error: "Network error. Please try again." };
  }
};

// ── Admin ─────────────────────────────────────────────────────────────────

const buildQuery = (params: Record<string, unknown>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === "all") return;
    qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export const getCaresReportsList = (
  filters: Pick<
    CaresReportsListFilters,
    "search" | "type" | "status" | "contextType" | "dateFrom" | "dateTo" | "page" | "pageSize"
  >,
) => {
  const query = buildQuery(filters);
  return v2Request(`/cares${query}`);
};

export const getCaresReportCounts = () => {
  return v2Request(`/cares/counts`);
};

export const getCaresReport = (id: number) => {
  return v2Request(`/cares/${id}`);
};

export const updateCaresReport = (
  id: number,
  body: { status?: string; adminInternalNotes?: string },
) => {
  return v2Request(`/cares/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  });
};
